import DeferredCanvas from "./DeferredCanvas";
import ThemeToggle from "./ThemeToggle";
import ScrollIndicator from "./ScrollIndicator";
import WaitlistForm from "./WaitlistForm";

const Hero = () => (
  <section className="relative min-h-dvh flex flex-col overflow-hidden" id="waitlist">
    <DeferredCanvas loader={() => import("./AntConstellation")} />
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -left-32 top-16 w-[420px] h-[420px] rounded-full bg-accent/[0.07] blur-[120px]" />
      <div className="absolute -right-32 top-24 w-[360px] h-[360px] rounded-full bg-[hsl(175_65%_45%/0.05)] blur-[120px]" />
    </div>

    {/* Main content — vertically centered */}
    <div className="flex-1 flex items-center justify-center pt-16">
      <div className="container-wide relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Logo icon */}
          <div className="flex justify-center mb-10 md:mb-12 animate-fade-in-up" style={{ animationDelay: "0s" }}>
            <div className="relative w-[72px] h-[72px] md:w-[96px] md:h-[96px] animate-ant-float">
              <div className="absolute inset-0 rounded-full blur-lg scale-125 bg-accent/[0.06]" />
              <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative w-full h-full drop-shadow-[0_0_24px_hsl(16_85%_56%/0.5)]">
                <ellipse cx="60" cy="88" rx="15" ry="13" fill="url(#ant-icon-grad)" />
                <ellipse cx="60" cy="64" rx="11" ry="10" fill="url(#ant-icon-grad)" />
                <ellipse cx="60" cy="44" rx="13" ry="12" fill="url(#ant-icon-grad)" />
                <path d="M49 60 Q34 52 24 46" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 49 60;-4 49 60;0 49 60;4 49 60;0 49 60" dur="3s" repeatCount="indefinite" />
                </path>
                <path d="M49 66 Q31 66 20 60" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 49 66;3 49 66;0 49 66;-3 49 66;0 49 66" dur="2.8s" repeatCount="indefinite" />
                </path>
                <path d="M49 74 Q34 80 26 88" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 49 74;-3 49 74;0 49 74;3 49 74;0 49 74" dur="3.2s" repeatCount="indefinite" />
                </path>
                <path d="M71 60 Q86 52 96 46" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 71 60;4 71 60;0 71 60;-4 71 60;0 71 60" dur="3s" repeatCount="indefinite" />
                </path>
                <path d="M71 66 Q89 66 100 60" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 71 66;-3 71 66;0 71 66;3 71 66;0 71 66" dur="2.8s" repeatCount="indefinite" />
                </path>
                <path d="M71 74 Q86 80 94 88" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" values="0 71 74;3 71 74;0 71 74;-3 71 74;0 71 74" dur="3.2s" repeatCount="indefinite" />
                </path>
                <g>
                  <animateTransform attributeName="transform" type="rotate" values="0 52 36;-8 52 36;0 52 36;5 52 36;0 52 36" dur="4s" repeatCount="indefinite" />
                  <path d="M52 36 Q40 18 30 10" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="30" cy="10" r="3.5" fill="hsl(16 85% 56%)" />
                </g>
                <g>
                  <animateTransform attributeName="transform" type="rotate" values="0 68 36;8 68 36;0 68 36;-5 68 36;0 68 36" dur="4s" repeatCount="indefinite" />
                  <path d="M68 36 Q80 18 90 10" stroke="hsl(16 85% 56%)" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="90" cy="10" r="3.5" fill="hsl(16 85% 56%)" />
                </g>
                <circle cx="52" cy="41" r="5" fill="hsl(222 30% 6%)" />
                <circle cx="68" cy="41" r="5" fill="hsl(222 30% 6%)" />
                <circle cx="53" cy="40" r="2" fill="hsl(175 65% 45%)" />
                <circle cx="69" cy="40" r="2" fill="hsl(175 65% 45%)" />
                <path d="M53 54 Q48 60 44 58" stroke="hsl(16 85% 56%)" strokeWidth="2" strokeLinecap="round" />
                <path d="M67 54 Q72 60 76 58" stroke="hsl(16 85% 56%)" strokeWidth="2" strokeLinecap="round" />
                <defs>
                  <linearGradient id="ant-icon-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(16 85% 60%)" />
                    <stop offset="100%" stopColor="hsl(16 85% 40%)" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-none mb-3 md:mb-4 animate-fade-in-up shimmer-text"
            style={{ fontFamily: "'Clash Display', 'Lexend', system-ui, sans-serif", animationDelay: "0.05s" }}
          >
            Ant<span className="text-accent">Atlas</span>
          </h1>

          {/* Accent subtitle */}
          <p
            className="text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-accent mb-5 md:mb-6 animate-fade-in-up"
            style={{ fontFamily: "'Clash Display', 'Lexend', system-ui, sans-serif", animationDelay: "0.12s" }}
          >
            The live context layer for AI coding agents
          </p>

          {/* Subheadline — LCP element. No animation/delay so it paints immediately. */}
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto mb-8 md:mb-10 leading-relaxed">
            Give every coding agent the context it needs, without repeatedly exploring the codebase or wasting tokens.
          </p>

          {/* CTA */}
          <div>
            <WaitlistForm compact source="hero" />
            <p className="text-xs text-muted-foreground/60 mt-4 tracking-wide">
              Early access · Product updates only · No spam
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom bar — pinned to bottom (safe-area aware for mobile browsers) */}
    <div
      className="relative z-50 pt-8 pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:pb-[calc(1.5rem+env(safe-area-inset-bottom))] animate-fade-in"
      style={{ animationDelay: "0.6s" }}
    >
      <div className="container-wide flex items-center justify-between">
        <ThemeToggle />
        <ScrollIndicator />
      </div>
    </div>
  </section>
);

export default Hero;
